import moment from 'moment'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router'

import { EuiButton, EuiFlexGroup, EuiFlexItem, EuiHorizontalRule, EuiSpacer, EuiText, EuiTitle, EuiLoadingContent } from '@elastic/eui'

import { MasterCampaignPlanResourceType } from 'api/entities/MasterCampaignPlan'
import { MasterCampaignPlanCampaignBuilder } from 'api/entities/MasterCampaignStub'
import { useGetMasterCampaignPlanQuery } from 'api/masterCampaignPlansApi'
import { getMasterCampaignStubAverageMonthlyBudgetOld } from 'utils/CampaignMathUtils'
import { calculateEndDate } from 'utils/MasterCampaignPlanUtils'
import { downloadPdf as downloadPdfUtil, fetchMasterCampaignPlanPdfBlob } from 'utils/downloadPdf'

import { CampaignCardOld } from './components/CampaignCardOld'
import GanttChartOld from './components/GanttChartOld'
import { ResearchDocumentSectionOld } from './components/ResearchDocumentSectionOld'
import { SummarySectionOld } from './components/SummarySectionOld'

type MasterCampaignPlanPageParams = {
  id: string
}
const SLICE_CAMPAIGNS_COUNT = 3

const BaseNotFound = 404
const MasterCampaignPlanNotFoundStatus = 1404
const MasterCampaignPlanInEditModeStatus = 1412
const MasterCampaignPlanInDspStatus = 1410

enum ErrorState {
  NotFound = 'NotFound',
  InEditMode = 'InEditMode',
  InDsp = 'InDsp'
}

export const MasterCampaignPlanPageOld = () => {
  const [errorState, setErrorState] = useState<ErrorState>()
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  })

  const { id } = useParams<MasterCampaignPlanPageParams>()

  const { data: masterCampaignPlan, isFetching: isMasterCampaignPlanFetching, error: error } = useGetMasterCampaignPlanQuery({ publicId: id ?? '' }, { skip: !id })

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    if (error?.originalStatus) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      const errorStatus = error.originalStatus
      if (errorStatus === MasterCampaignPlanNotFoundStatus || errorStatus === BaseNotFound) {
        setErrorState(ErrorState.NotFound)
      } else if (errorStatus === MasterCampaignPlanInEditModeStatus) {
        setErrorState(ErrorState.InEditMode)
      } else if (errorStatus === MasterCampaignPlanInDspStatus) {
        setErrorState(ErrorState.InDsp)
      }
    } else {
      setErrorState(undefined)
    }
  }, [error])

  const masterCampaignStub = masterCampaignPlan?.masterCampaignStub

  const [viewAll, setViewAll] = useState(false)

  const averageMonthlyBudget = masterCampaignPlan && masterCampaignStub ? getMasterCampaignStubAverageMonthlyBudgetOld(masterCampaignPlan, masterCampaignStub) : 0

  const listCampaigns = [...(masterCampaignStub?.campaignBuilders ?? [])]
    .sort((a, b) => moment(a.startDate).valueOf() - moment(b.startDate).valueOf())
    .slice(0, viewAll && masterCampaignStub?.campaignBuilders ? masterCampaignStub?.campaignBuilders.length : SLICE_CAMPAIGNS_COUNT)
    .map(builder => {
      return (
        <>
          <CampaignCardOld builder={builder} key={builder.id} />
          <EuiSpacer size={'m'} />
        </>
      )
    })

  const downloadPdf = async ({ masterCampaignPlanName, fileName, masterCampaignPlanResourceType }: { masterCampaignPlanName?: string; url?: string; fileName: string; masterCampaignPlanResourceType: MasterCampaignPlanResourceType }) => {
    if (!masterCampaignPlanName || !id) {
      return
    }
    const blob = await fetchMasterCampaignPlanPdfBlob(id, masterCampaignPlanResourceType)
    downloadPdfUtil(blob, `${masterCampaignPlanName}_${fileName}`)
  }

  if (isMasterCampaignPlanFetching && !masterCampaignPlan) {
    return <EuiLoadingContent />
  }

  return (
    <>
      {!errorState && masterCampaignPlan && (
        <>
          <EuiFlexGroup>
            <EuiFlexItem>
              <EuiTitle>
                <h2>{masterCampaignPlan?.name}</h2>
              </EuiTitle>
            </EuiFlexItem>
          </EuiFlexGroup>
          <EuiSpacer />
          <EuiFlexGroup gutterSize={'xl'} justifyContent='flexStart'>
            <EuiFlexItem>
              <GanttChartOld
                chartItems={
                  masterCampaignStub?.campaignBuilders?.map((c: MasterCampaignPlanCampaignBuilder) => {
                    return {
                      name: c.name,
                      type: c.campaignType,
                      startDate: moment(c.startDate),
                      endDate: calculateEndDate(c)
                    }
                  }) ?? []
                }
              />
              <EuiSpacer size={'xxl'} />
              <SummarySectionOld title='Plan Summary' content={masterCampaignPlan?.planSummary} />
              <SummarySectionOld title='Geo Targeting' content={masterCampaignPlan?.geoSummary} />
              <SummarySectionOld title='Audience Targeting' content={masterCampaignPlan?.audienceSummary} />
              <SummarySectionOld title='Seasonality' content={masterCampaignPlan?.seasonalitySummary} />
              <div>
                <EuiFlexGroup direction='column'>
                  <EuiTitle size={'xs'}>
                    <p>Supporting Documents</p>
                  </EuiTitle>
                  <EuiText>The following documents are associated with this marketing plan and provide the context and rationale for the campaigns.</EuiText>
                </EuiFlexGroup>
                <EuiSpacer size={'xl'} />
                <div style={{ marginLeft: '15px' }}>
                  <ResearchDocumentSectionOld
                    title='Campaign Details'
                    description='A detailed description of each campaign showing the targeted audience, geo-targeting, dates, budget, and other considerations for each campaign.'
                    onDownload={async () => {
                      await downloadPdf({
                        masterCampaignPlanName: masterCampaignPlan?.name,
                        masterCampaignPlanResourceType: MasterCampaignPlanResourceType.CampaignDetailsAndRationale,
                        fileName: 'CampaignDetailsAndRationale.pdf'
                      })
                    }}
                    isDownloadDisabled={!masterCampaignPlan?.name || !masterCampaignPlan?.campaignDetailsAndRationaleAvailable}
                  />
                  <EuiSpacer size={'m'} />
                  <ResearchDocumentSectionOld
                    title='Marketing Plan'
                    description='A summary of the marketing plan that includes specific details on the audience targeting, geo-targeting approach and seasonal considerations. It also includes a timeline of the campaigns.'
                    onDownload={async () => {
                      await downloadPdf({
                        masterCampaignPlanName: masterCampaignPlan?.name,
                        masterCampaignPlanResourceType: MasterCampaignPlanResourceType.MarketingPlan,
                        fileName: 'MarketingPlan.pdf'
                      })
                    }}
                    isDownloadDisabled={!masterCampaignPlan?.name || !masterCampaignPlan?.marketingPlanAvailable}
                  />
                  <EuiSpacer size={'m'} />
                  <ResearchDocumentSectionOld
                    title='Market Analysis & Targeting'
                    description='Complete research on the market conditions and targeting recommendations associated with this marketing campaign.'
                    onDownload={async () => {
                      await downloadPdf({
                        masterCampaignPlanName: masterCampaignPlan?.name,
                        masterCampaignPlanResourceType: MasterCampaignPlanResourceType.MarketAnalysis,
                        fileName: 'MarketAnalysis.pdf'
                      })
                    }}
                    isDownloadDisabled={!masterCampaignPlan?.name || !masterCampaignPlan?.marketAnalysisAvailable}
                  />
                </div>
              </div>
            </EuiFlexItem>
            <EuiFlexItem grow={false} />
            <EuiFlexItem>
              <EuiTitle size={'s'}>
                <p>Campaigns</p>
              </EuiTitle>
              <EuiSpacer size='m' />
              <EuiText>
                Based on a targeted monthly budget, this plan includes {masterCampaignStub?.campaignBuilders.length} campaigns below with an average monthly spend of {formatter.format(averageMonthlyBudget)}. Each campaign has specific geographic targeting and audience segments based on the marketing strategy.
              </EuiText>

              <EuiHorizontalRule />

              <EuiFlexGroup>
                <EuiFlexItem>{listCampaigns}</EuiFlexItem>
              </EuiFlexGroup>

              {(masterCampaignStub?.campaignBuilders?.length ?? 0) > SLICE_CAMPAIGNS_COUNT && (
                <EuiFlexGroup>
                  <EuiFlexItem />
                  <EuiFlexItem grow={false}>
                    <EuiButton fill onClick={() => setViewAll(!viewAll)}>
                      {viewAll ? 'View Fewer Campaigns' : `View All ${masterCampaignStub?.campaignBuilders?.length} Campaigns`}
                    </EuiButton>
                  </EuiFlexItem>
                  <EuiFlexItem />
                </EuiFlexGroup>
              )}
            </EuiFlexItem>
          </EuiFlexGroup>
        </>
      )}

      {errorState === ErrorState.NotFound && (
        <EuiFlexGroup
          style={{
            height: '100%'
          }}
          direction='column'
          alignItems='center'
          justifyContent='center'>
          <EuiFlexItem grow={false}>
            <EuiTitle size='l'>
              <h1>The plan you are attempting to view cannot be found.</h1>
            </EuiTitle>
          </EuiFlexItem>
        </EuiFlexGroup>
      )}
      {errorState === ErrorState.InEditMode && (
        <EuiFlexGroup
          style={{
            height: '100%'
          }}
          direction='column'
          alignItems='center'
          justifyContent='center'>
          <EuiFlexItem grow={false}>
            <EuiTitle size='l'>
              <h1>This plan is currently being edited.</h1>
            </EuiTitle>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiText size='m'>
              <p>The plan will become viewable again once the edits have been completed.</p>
            </EuiText>
          </EuiFlexItem>
        </EuiFlexGroup>
      )}
      {errorState === ErrorState.InDsp && (
        <EuiFlexGroup
          style={{
            height: '100%'
          }}
          direction='column'
          alignItems='center'
          justifyContent='center'>
          <EuiFlexItem grow={false}>
            <EuiTitle size='l'>
              <h1>This plan is no longer viewable.</h1>
            </EuiTitle>
          </EuiFlexItem>
        </EuiFlexGroup>
      )}
    </>
  )
}
